import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Section, Eyebrow } from "@/components/Primitives";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Linkedin, Facebook, Instagram, Globe, Twitter, MapPin, Star, ShieldCheck, ShieldAlert, Upload, Camera, CheckCircle2, MessageSquare, Send } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const ROLES = ["homeowner", "contractor", "laborer", "realtor", "driver"];
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const Profile = () => {
  const { userId } = useParams();
  const { user, updateUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [verif, setVerif] = useState({ verification_status: "unverified", verification_docs: {} });
  const wsibRef = useRef(); const insRef = useRef(); const avatarRef = useRef();
  const navigate = useNavigate();
  const [reviews, setReviews] = useState([]);
  const [reviewForm, setReviewForm] = useState({ rating: 5, comment: "" });

  const targetId = userId || user?.user_id;

  const isOwn = !userId || (user && userId === user.user_id);

  useEffect(() => {
    if (isOwn) {
      if (user) {
        setProfile(user);
        setForm({ name: user.name || "", role: user.role, bio: user.bio || "", phone: user.phone || "", location: user.location || "", company: user.company || "", headline: user.headline || "", avatar: user.avatar || "", banner: user.banner || "", skills: (user.skills || []).join(", "), social: { linkedin: user.social?.linkedin || "", facebook: user.social?.facebook || "", instagram: user.social?.instagram || "", website: user.social?.website || "", twitter: user.social?.twitter || "" } });
        api.get("/verification/me").then((r) => setVerif(r.data)).catch(() => {});
      }
    } else {
      api.get(`/users/${userId}`).then((r) => setProfile(r.data)).catch(() => {});
    }
  }, [userId, user, isOwn]);

  useEffect(() => {
    if (targetId) api.get(`/reviews/${targetId}`).then((r) => setReviews(r.data)).catch(() => {});
  }, [targetId]);

  const submitReview = async () => {
    if (!userId) return;
    try {
      await api.post("/reviews", { target_user_id: userId, rating: reviewForm.rating, comment: reviewForm.comment });
      toast.success("Review posted!");
      setReviewForm({ rating: 5, comment: "" });
      const [rv, pf] = await Promise.all([api.get(`/reviews/${userId}`), api.get(`/users/${userId}`)]);
      setReviews(rv.data); setProfile(pf.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not post review"); }
  };

  if (!profile) return <div className="min-h-[60vh] flex items-center justify-center text-slate-400">Loading profile...</div>;

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, skills: form.skills.split(",").map((s) => s.trim()).filter(Boolean) };
      const { data } = await api.put("/auth/profile", payload);
      updateUser(data); setProfile(data);
      toast.success("Profile saved!");
    } catch (e) { toast.error("Could not save profile"); }
    finally { setSaving(false); }
  };

  const uploadImage = async (file, field) => {
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = `${BACKEND}${data.url}`;
      const { data: updated } = await api.put("/auth/profile", { [field]: url });
      updateUser(updated); setProfile(updated); setForm({ ...form, [field]: url });
      toast.success("Image updated!");
    } catch (e) { toast.error("Upload failed"); }
  };

  const uploadDoc = async (file, docType) => {
    const fd = new FormData(); fd.append("file", file); fd.append("doc_type", docType);
    try {
      const { data } = await api.post("/verification/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setVerif(data);
      const { data: me } = await api.get("/auth/me"); updateUser(me); setProfile(me);
      if (data.verification_status === "verified") toast.success("Verified! You can now connect with customers.");
      else if (data.verification_status === "pending") toast.success("Both documents submitted — pending admin approval.");
      else toast.success(`${docType.toUpperCase()} uploaded. Submit both docs for review.`);
    } catch (e) { toast.error("Upload failed"); }
  };

  const socials = [
    ["linkedin", Linkedin], ["facebook", Facebook], ["instagram", Instagram], ["twitter", Twitter], ["website", Globe],
  ];

  return (
    <div className="pb-16" data-testid="profile-page">
      {/* Banner */}
      <div className="relative h-48 sm:h-64 eti-grid-bg border-b border-white/10 overflow-hidden">
        {profile.banner ? <img src={profile.banner} alt="" className="w-full h-full object-cover" /> : <div className="absolute inset-0 eti-hazard opacity-10" />}
        {isOwn && (
          <>
            <input ref={(r) => (avatarRef.banner = r)} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && uploadImage(e.target.files[0], "banner")} data-testid="profile-banner-upload" />
            <button onClick={() => avatarRef.banner?.click()} className="absolute top-4 right-4 bg-black/60 text-white rounded-md px-3 py-1.5 text-xs flex items-center gap-1.5 hover:bg-black/80"><Camera className="h-3.5 w-3.5" />Edit Cover</button>
          </>
        )}
      </div>

      <Section className="-mt-16 relative">
        <div className="flex flex-col sm:flex-row items-start sm:items-end gap-5">
          <div className="relative">
            <Avatar className="h-32 w-32 border-4 border-[#0b0d10] rounded-xl">
              <AvatarImage src={profile.avatar} />
              <AvatarFallback className="bg-primary/20 text-primary text-4xl rounded-xl">{profile.name?.[0]?.toUpperCase()}</AvatarFallback>
            </Avatar>
            {isOwn && (
              <>
                <input ref={avatarRef} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && uploadImage(e.target.files[0], "avatar")} data-testid="profile-avatar-upload" />
                <button onClick={() => avatarRef.current?.click()} className="absolute bottom-1 right-1 bg-primary text-white rounded-full h-8 w-8 flex items-center justify-center"><Camera className="h-4 w-4" /></button>
              </>
            )}
          </div>
          <div className="flex-1 pb-2">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="font-display text-3xl font-extrabold uppercase tracking-tight text-white">{profile.name}</h1>
              {profile.role === "contractor" && profile.verification_status === "verified" && (
                <Badge className="bg-emerald-500/15 text-emerald-400 border-0" data-testid="profile-verified-badge"><ShieldCheck className="h-3 w-3 mr-1" />WSIB & Insured</Badge>
              )}
            </div>
            <p className="text-slate-400 mt-1 flex items-center gap-3 flex-wrap text-sm">
              <span className="capitalize">{profile.role}</span>
              {profile.location && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{profile.location}</span>}
              <span className="flex items-center gap-1 text-primary"><Star className="h-3.5 w-3.5 fill-primary" />{profile.rating} <span className="text-slate-500">({profile.review_count || 0})</span></span>
            </p>
            {profile.headline && <p className="text-slate-300 mt-1">{profile.headline}</p>}
          </div>
          {!isOwn && user && (
            <Button onClick={() => navigate(`/messages?to=${profile.user_id}`)} className="bg-primary hover:bg-primary/90 text-white font-semibold mb-2" data-testid="profile-message-button"><MessageSquare className="h-4 w-4 mr-2" />Message</Button>
          )}
        </div>

        {/* Social links */}
        <div className="flex gap-2 mt-5">
          {socials.map(([key, Icon]) => profile.social?.[key] ? (
            <a key={key} href={profile.social[key]} target="_blank" rel="noreferrer" className="h-9 w-9 rounded-md border border-white/10 flex items-center justify-center text-slate-300 hover:text-primary hover:border-primary transition-colors" data-testid={`profile-social-${key}-link`}>
              <Icon className="h-4 w-4" />
            </a>
          ) : null)}
        </div>

        <div className="grid lg:grid-cols-3 gap-6 mt-8">
          <div className="lg:col-span-2">
            {isOwn ? (
              <Tabs defaultValue="edit">
                <TabsList className="bg-secondary">
                  <TabsTrigger value="edit" className="data-[state=active]:bg-primary data-[state=active]:text-white" data-testid="profile-tab-edit">Edit Profile</TabsTrigger>
                  {profile.role === "contractor" && <TabsTrigger value="verify" className="data-[state=active]:bg-primary data-[state=active]:text-white" data-testid="profile-tab-verify">Verification</TabsTrigger>}
                </TabsList>

                <TabsContent value="edit" className="mt-5 space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div><Label className="text-xs text-slate-300">Name / Company</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-secondary border-white/10 mt-1" data-testid="profile-name-input" /></div>
                    <div><Label className="text-xs text-slate-300">Role</Label><Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}><SelectTrigger className="bg-secondary border-white/10 mt-1" data-testid="profile-role-select"><SelectValue /></SelectTrigger><SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r} className="capitalize">{r}</SelectItem>)}</SelectContent></Select></div>
                  </div>
                  <div><Label className="text-xs text-slate-300">Headline</Label><Input value={form.headline} onChange={(e) => setForm({ ...form, headline: e.target.value })} placeholder="e.g. Licensed GC · 15 yrs · Kitchens & Baths" className="bg-secondary border-white/10 mt-1" data-testid="profile-headline-input" /></div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div><Label className="text-xs text-slate-300">Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-secondary border-white/10 mt-1" data-testid="profile-phone-input" /></div>
                    <div><Label className="text-xs text-slate-300">Location</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="bg-secondary border-white/10 mt-1" data-testid="profile-location-input" /></div>
                  </div>
                  <div><Label className="text-xs text-slate-300">Bio</Label><Textarea value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} className="bg-secondary border-white/10 mt-1" data-testid="profile-bio-input" /></div>
                  <div><Label className="text-xs text-slate-300">Skills (comma separated)</Label><Input value={form.skills} onChange={(e) => setForm({ ...form, skills: e.target.value })} className="bg-secondary border-white/10 mt-1" data-testid="profile-skills-input" /></div>
                  <div>
                    <Label className="text-xs text-slate-300 mb-2 block">Social Links</Label>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {socials.map(([key, Icon]) => (
                        <div key={key} className="flex items-center gap-2">
                          <Icon className="h-4 w-4 text-slate-400 shrink-0" />
                          <Input value={form.social[key]} onChange={(e) => setForm({ ...form, social: { ...form.social, [key]: e.target.value } })} placeholder={key} className="bg-secondary border-white/10" data-testid={`profile-social-input-${key}`} />
                        </div>
                      ))}
                    </div>
                  </div>
                  <Button onClick={save} disabled={saving} className="bg-primary hover:bg-primary/90 text-white font-semibold" data-testid="profile-save-button">{saving ? "Saving..." : "Save Profile"}</Button>
                </TabsContent>

                {profile.role === "contractor" && (
                  <TabsContent value="verify" className="mt-5">
                    <div className="eti-card rounded-lg p-6">
                      <div className="flex items-center gap-2 mb-4">
                        {verif.verification_status === "verified"
                          ? <Badge className="bg-emerald-500/15 text-emerald-400 border-0"><ShieldCheck className="h-3 w-3 mr-1" />Verified</Badge>
                          : verif.verification_status === "pending"
                          ? <Badge className="bg-amber-500/15 text-amber-400 border-0"><ShieldAlert className="h-3 w-3 mr-1" />Pending Admin Approval</Badge>
                          : verif.verification_status === "rejected"
                          ? <Badge className="bg-red-500/15 text-red-400 border-0"><ShieldAlert className="h-3 w-3 mr-1" />Rejected — Please Re-upload</Badge>
                          : <Badge className="bg-primary/15 text-primary border-0"><ShieldAlert className="h-3 w-3 mr-1" />Not Verified</Badge>}
                      </div>
                      <p className="text-sm text-slate-400 mb-5">Contractors must submit both a WSIB clearance certificate and commercial liability insurance to access customer job listings.</p>
                      <div className="grid sm:grid-cols-2 gap-4">
                        {[["wsib", "WSIB Certificate", wsibRef], ["insurance", "Liability Insurance", insRef]].map(([type, label, ref]) => (
                          <div key={type} className="border border-dashed border-white/15 rounded-lg p-5 text-center">
                            {verif.verification_docs?.[type] ? <CheckCircle2 className="h-8 w-8 text-emerald-400 mx-auto" /> : <Upload className="h-8 w-8 text-slate-500 mx-auto" />}
                            <p className="text-sm text-white font-medium mt-2">{label}</p>
                            <p className="text-xs text-slate-500 mb-3">{verif.verification_docs?.[type] ? verif.verification_docs[type].filename || "Uploaded" : "PDF, JPG or PNG"}</p>
                            <input ref={ref} type="file" accept=".pdf,image/*" className="hidden" onChange={(e) => e.target.files[0] && uploadDoc(e.target.files[0], type)} data-testid={`${type}-upload-input`} />
                            <Button size="sm" variant="outline" onClick={() => ref.current?.click()} className="border-white/15 text-white" data-testid={`${type}-upload-button`}>{verif.verification_docs?.[type] ? "Replace" : "Upload"}</Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </TabsContent>
                )}
              </Tabs>
            ) : (
              <div className="eti-card rounded-lg p-6">
                <h3 className="font-display font-bold text-white uppercase mb-3">About</h3>
                <p className="text-slate-300 leading-relaxed">{profile.bio || "No bio yet."}</p>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            <div className="eti-card rounded-lg p-5">
              <h4 className="font-display font-bold text-white uppercase text-sm mb-3">Details</h4>
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between"><dt className="text-slate-500">Role</dt><dd className="text-white capitalize">{profile.role}</dd></div>
                {profile.company && <div className="flex justify-between"><dt className="text-slate-500">Company</dt><dd className="text-white">{profile.company}</dd></div>}
                {profile.phone && <div className="flex justify-between"><dt className="text-slate-500">Phone</dt><dd className="text-white">{profile.phone}</dd></div>}
                <div className="flex justify-between"><dt className="text-slate-500">Rating</dt><dd className="text-primary flex items-center gap-1"><Star className="h-3.5 w-3.5 fill-primary" />{profile.rating}</dd></div>
              </dl>
            </div>
            {profile.skills?.length > 0 && (
              <div className="eti-card rounded-lg p-5">
                <h4 className="font-display font-bold text-white uppercase text-sm mb-3">Skills</h4>
                <div className="flex flex-wrap gap-2">
                  {profile.skills.map((s, i) => <Badge key={i} variant="outline" className="border-white/15 text-slate-200">{s}</Badge>)}
                </div>
              </div>
            )}
            {profile.bio && isOwn && (
              <div className="eti-card rounded-lg p-5">
                <h4 className="font-display font-bold text-white uppercase text-sm mb-2">About</h4>
                <p className="text-sm text-slate-400 leading-relaxed">{profile.bio}</p>
              </div>
            )}
          </div>
        </div>

        {/* Reviews */}
        <div className="mt-12" data-testid="profile-reviews">
          <h3 className="font-display font-bold text-white uppercase text-lg mb-1">Ratings & Reviews</h3>
          <p className="text-sm text-slate-400 mb-5 flex items-center gap-1"><Star className="h-4 w-4 text-primary fill-primary" />{profile.rating} · {reviews.length} review{reviews.length === 1 ? "" : "s"}</p>

          {!isOwn && user && (
            <div className="eti-card rounded-lg p-5 mb-5" data-testid="review-form">
              <p className="text-sm font-semibold text-white mb-2">Leave a review</p>
              <div className="flex gap-1 mb-3">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} onClick={() => setReviewForm({ ...reviewForm, rating: n })} data-testid={`review-star-${n}`}>
                    <Star className={`h-6 w-6 ${n <= reviewForm.rating ? "text-primary fill-primary" : "text-slate-600"}`} />
                  </button>
                ))}
              </div>
              <Textarea value={reviewForm.comment} onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })} placeholder="Share your experience..." className="bg-secondary border-white/10" data-testid="review-comment-input" />
              <Button onClick={submitReview} className="mt-3 bg-primary hover:bg-primary/90 text-white font-semibold" data-testid="review-submit-button"><Send className="h-4 w-4 mr-2" />Post Review</Button>
            </div>
          )}

          <div className="space-y-3">
            {reviews.length === 0 && <p className="text-sm text-slate-500">No reviews yet.</p>}
            {reviews.map((r) => (
              <div key={r.id} className="eti-card rounded-lg p-4" data-testid={`review-${r.id}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Avatar className="h-8 w-8"><AvatarImage src={r.author_avatar} /><AvatarFallback className="bg-primary/20 text-primary text-xs">{r.author_name?.[0]}</AvatarFallback></Avatar>
                    <span className="text-sm font-semibold text-white">{r.author_name}</span>
                  </div>
                  <span className="flex items-center gap-0.5 text-primary text-sm">{Array.from({ length: r.rating }).map((_, i) => <Star key={i} className="h-3.5 w-3.5 fill-primary" />)}</span>
                </div>
                {r.comment && <p className="text-sm text-slate-300 mt-2">{r.comment}</p>}
              </div>
            ))}
          </div>
        </div>
      </Section>
    </div>
  );
};

export default Profile;
